import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import moment from 'moment'

import { LoadingLabel } from './loader'
import UsageReportCallouts from './usage-report-callouts'
import UsageReportGraph from './usage-report-graph'
import { getCurrentAccountQRDetails } from '../api/apollo/graphql/company-client'
import uplifter from '../assets/uplogo.png'
import {
  dateFormatShort,
  performanceDates,
  siteContainerWidth,
} from '../core/variables'
import { getItemByKeyValue } from '../helpers'
import { Logo } from '../pages/performance-report-page'
import styles from '../styles/usage-report-page.module.scss'
import { GetAccountUsageDataQuery } from '../__gql-types__/graphql'

export const successMetrics = [
  'badCodesBlocked',
  'invalidCodesBreakdown',
  'codesCreated',
  'logins',
  'activeUsers',
] as const
type UsageSuccessMetricsTuple = typeof successMetrics
export type UsageSuccessMetrics = UsageSuccessMetricsTuple[number]

interface UsageReportTabProps {
  loading?: boolean
  accountUsageData: GetAccountUsageDataQuery['report'] | null
  width: number
  screenWidth: number
  isMobile: boolean
  successMetric: UsageSuccessMetrics
  granularity: Granularity
  stackDimension: string
  graphDateRange: string[] | null
  graphData: DashboardGraphData[] | null
  graphTableTotals: number[] | null
  startDate: Date
  endDate: Date
  currentDate: string
}

const UsageReportTab = ({
  loading = false,
  accountUsageData,
  width,
  screenWidth,
  isMobile,
  successMetric = 'invalidCodesBreakdown',
  granularity = 'monthly',
  stackDimension = 'account',
  graphDateRange,
  graphData,
  graphTableTotals,
  startDate,
  endDate,
  currentDate,
}: UsageReportTabProps): React.ReactElement => {
  const graphWidth: number = useMemo(() => {
    let res: number
    if (width) {
      res = width
    } else if (screenWidth > 1260) {
      res = 1108
    } else if (screenWidth < siteContainerWidth) {
      res = screenWidth - 40
    } else if (screenWidth <= siteContainerWidth + 150) {
      res = screenWidth - 150
    } else {
      res = 960
    }
    return res - 34 - 40
  }, [width, screenWidth])

  let offset = granularity === 'monthly' || granularity === 'quarterly' ? 0 : 40

  if (isMobile && offset > 0) {
    offset = 20
  }

  // TODO: Refine this query, it doesn't need all the QR info
  const { data: logoData } = useQuery(getCurrentAccountQRDetails)

  const useLogo = logoData?.currentAccount.logoLink || uplifter
  const logoLink =
    useLogo.indexOf('logos/PNG/uplogo.png') !== -1 ? uplifter : useLogo

  const dataBasedDateRange = useMemo(() => {
    if (loading) return ''

    let queryStartDate
    let queryEndDate

    if (
      accountUsageData &&
      accountUsageData[successMetric] &&
      accountUsageData[successMetric]?.queryStartDate
    ) {
      queryStartDate = accountUsageData[successMetric]?.queryStartDate
      queryEndDate = accountUsageData[successMetric]?.queryEndDate
    }

    if (!queryEndDate) {
      return ''
    }

    const start = moment(queryStartDate, 'YYYY-MM-DD').format(dateFormatShort)
    const end = moment(queryEndDate, 'YYYY-MM-DD').format(dateFormatShort)

    return `${start} - ${end}`
  }, [accountUsageData, loading])

  const dateRangeLabel = useMemo(() => {
    const currentDateItem: PerformanceDate = getItemByKeyValue(
      performanceDates,
      'metricValue',
      currentDate,
    )
    if (
      currentDateItem &&
      currentDateItem.label &&
      currentDateItem.metricValue
    ) {
      if (currentDateItem.metricValue === 'custom') {
        const from = moment(startDate).format(dateFormatShort)
        const to = moment(endDate).format(dateFormatShort)
        return ` from ${from} to ${to}`
      }

      return ` over the ${currentDateItem.label}`
    }
    return ''
  }, [currentDate, startDate, endDate])

  const { successMetricDisplayName, successMetricDescription } = useMemo(() => {
    switch (successMetric) {
      case 'badCodesBlocked':
        return {
          successMetricDisplayName: 'Invalid links prevented',
          successMetricDescription:
            'The number of links blocked to stop broken customer journeys and/or tracking.',
        }
      case 'invalidCodesBreakdown':
        return {
          successMetricDisplayName: 'Link errors breakdown',
          successMetricDescription:
            'What sort of errors were triggered by users when trying to create new links.',
        }
      case 'codesCreated':
        return {
          successMetricDisplayName: 'Campaign links created',
          successMetricDescription:
            'The number of unique campaign links you have created in Uplifter.',
        }
      case 'activeUsers':
        return {
          successMetricDisplayName: 'Active users',
          successMetricDescription:
            'The number of users who have performed an action in Uplifter.',
        }
      default:
        return {
          successMetricDisplayName: 'Logins',
          successMetricDescription:
            'The number of times users of your account(s) have logged in to Uplifter.',
        }
    }
  }, [successMetric])

  return (
    <div className={styles.paper}>
      <div className={styles.paperHeader}>
        <div className={styles.paperHeaderContent}>
          <div className={styles.editTitleWrapper}>
            <h4>{successMetricDisplayName}</h4>
          </div>
          <p className={styles.dateRangePar}>
            <strong>Date range:</strong>{' '}
            {dataBasedDateRange && !loading ? (
              `${dataBasedDateRange}`
            ) : (
              <LoadingLabel label="Loading" />
            )}
          </p>
          {successMetricDisplayName && (
            <p>
              <strong>Description:</strong> {successMetricDescription}
            </p>
          )}
          {successMetric === 'logins' && (
            <p className={styles.metricNote}>
              Note: Data not available from 16th Feb - 4th Apr 2023
            </p>
          )}
        </div>
        <div className={styles.logoWrapper}>
          <Logo logoLink={logoLink} loading={!logoData} />
        </div>
      </div>
      <div className={styles.metricDataBannerWrapper}>
        <div className={styles.metricDataBanner}>
          <UsageReportCallouts
            successMetric={
              successMetric === 'activeUsers'
                ? 'users'
                : successMetricDisplayName
            }
            dateRange={dataBasedDateRange}
            data={
              accountUsageData && accountUsageData[successMetric]
                ? accountUsageData[
                    successMetric === 'activeUsers'
                      ? 'totalUsers'
                      : successMetric
                  ]?.total
                : null
            }
            loading={loading}
          />
          {successMetric === 'activeUsers' &&
            accountUsageData &&
            accountUsageData.activeUsers &&
            accountUsageData.totalUsers && (
              <>
                {/* Active users callout */}
                <UsageReportCallouts
                  successMetric="active users"
                  dateRange={dataBasedDateRange}
                  data={accountUsageData.activeUsers.total}
                  loading={loading}
                />
                {/* Dormant users callout */}
                <UsageReportCallouts
                  successMetric="dormant users"
                  dateRange={dataBasedDateRange}
                  data={
                    accountUsageData.totalUsers.total -
                    accountUsageData.activeUsers.total
                  }
                  loading={loading}
                />
              </>
            )}
        </div>
      </div>
      <div className={styles.graphWrapper}>
        <UsageReportGraph
          title={stackDimension === '' ? 'No breakdown' : stackDimension}
          loading={loading}
          width={width}
          graphWidth={graphWidth}
          offset={offset}
          granularity={granularity}
          isMobile={isMobile}
          graphDateRange={graphDateRange}
          graphData={graphData}
          graphTableTotals={graphTableTotals}
          stackDimension={stackDimension}
          successMetricDisplayName={successMetricDisplayName}
        >
          {loading ? (
            <></>
          ) : (
            <h1 className={styles.graphTitle}>
              <span>
                {successMetricDisplayName} {dateRangeLabel}{' '}
                {stackDimension !== '' && <>split by {stackDimension}</>}
              </span>
            </h1>
          )}
        </UsageReportGraph>
      </div>
    </div>
  )
}

export default UsageReportTab
